import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SOURCE_LABELS } from "../../shared/sources";
import { brandQuery, useBrand, type BrandListItem } from "../brandContext";
import { useToast } from "../toastContext";

function parseLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function BrandsPage() {
  const { toast } = useToast();
  const { brands, activeId, reload, activate, defaultSources } = useBrand();
  const [name, setName] = useState("");
  const [queriesText, setQueriesText] = useState("");
  const [feedsText, setFeedsText] = useState("");
  const [sources, setSources] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editQueries, setEditQueries] = useState("");
  const [editFeeds, setEditFeeds] = useState("");
  const [editSources, setEditSources] = useState<string[]>([]);

  useEffect(() => {
    if (defaultSources.length && !sources.length) {
      setSources([...defaultSources]);
    }
  }, [defaultSources, sources.length]);

  useEffect(() => {
    if (!name.trim()) {
      setSuggested([]);
      return;
    }
    const t = setTimeout(() => {
      void fetch("/api/brands/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      })
        .then((r) => r.json())
        .then((d) => setSuggested(d.queries || []));
    }, 250);
    return () => clearTimeout(t);
  }, [name]);

  function toggleSource(list: string[], id: string, set: (v: string[]) => void) {
    set(list.includes(id) ? list.filter((s) => s !== id) : [...list, id]);
  }

  function startEdit(b: BrandListItem) {
    setEditId(b.id);
    setEditQueries((b.queries || []).join("\n"));
    setEditFeeds((b.alertsFeedUrls || []).join("\n"));
    setEditSources(b.sources?.length ? [...b.sources] : [...defaultSources]);
    setMsg("");
  }

  return (
    <>
      <div className="page-head">
        <h2>Бренды</h2>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Подставьте любой бренд: название, поисковые ключи, источники и (опционально)
        RSS Google Alerts. Скан и таймер работают по каждому бренду отдельно.
      </p>
      <div className="grid-2">
        <div className="panel">
          <h3>Проекты</h3>
          {brands.map((b) => (
            <div className="plugin" key={b.id}>
              <div style={{ flex: 1 }}>
                <h4>
                  {b.name}{" "}
                  {b.id === activeId ? (
                    <span className="tag ok">активен</span>
                  ) : null}
                </h4>
                <p>{(b.queries || []).slice(0, 6).join(" · ") || "—"}</p>
                <p className="hint">
                  Источники: {(b.sources || []).join(", ") || "—"}
                  {(b.alertsFeedUrls?.length ?? 0) > 0
                    ? ` · alerts: ${b.alertsFeedUrls!.length}`
                    : ""}
                  {` · эталоны: ${b.assetsCount ?? 0}`}
                </p>
                {b.id === activeId ? (
                  <p className="hint">
                    Логотип и фото карточек этого бренда →{" "}
                    <Link to="/images">Картинки</Link>
                  </p>
                ) : null}
                {editId === b.id ? (
                  <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                    <label className="field">
                      <span>Запросы</span>
                      <textarea
                        rows={5}
                        value={editQueries}
                        onChange={(e) => setEditQueries(e.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>Google Alerts / RSS (https, по одному URL)</span>
                      <textarea
                        rows={3}
                        value={editFeeds}
                        onChange={(e) => setEditFeeds(e.target.value)}
                        placeholder="https://www.google.com/alerts/feeds/..."
                      />
                    </label>
                    <div className="field">
                      <span>Источники</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {defaultSources.map((id) => (
                          <label key={id} style={{ display: "flex", gap: 6 }}>
                            <input
                              type="checkbox"
                              checked={editSources.includes(id)}
                              onChange={() =>
                                toggleSource(editSources, id, setEditSources)
                              }
                            />
                            {SOURCE_LABELS[id] || id}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="btn primary"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            setMsg("");
                            try {
                              const r = await fetch(`/api/brands/${b.id}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  name: b.name,
                                  queries: parseLines(editQueries),
                                  alerts_feed_urls: parseLines(editFeeds),
                                  sources: editSources,
                                }),
                              });
                              const d = await r.json();
                              if (!r.ok) throw new Error(d.error || "fail");
                              setEditId(null);
                              setMsg("Сохранено");
                              await reload();
                            } catch (e) {
                              setMsg(e instanceof Error ? e.message : String(e));
                            } finally {
                              setBusy(false);
                            }
                          })();
                        }}
                      >
                        Сохранить
                      </button>
                      <button className="btn" onClick={() => setEditId(null)}>
                        Отмена
                      </button>
                      <button
                        className="btn"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            setMsg("");
                            try {
                              const r = await fetch("/api/scan", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ brandId: b.id }),
                              });
                              const d = await r.json();
                              if (!r.ok) throw new Error(d.error || "scan fail");
                              setMsg(
                                `Скан «${b.name}»: +${d.inserted ?? 0} / upd ${d.updated ?? 0}`,
                              );
                              await reload();
                            } catch (e) {
                              setMsg(e instanceof Error ? e.message : String(e));
                            } finally {
                              setBusy(false);
                            }
                          })();
                        }}
                      >
                        Сканировать
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {b.id !== activeId ? (
                  <button
                    className="btn primary"
                    onClick={() => void activate(b.id)}
                  >
                    Выбрать
                  </button>
                ) : null}
                {editId !== b.id ? (
                  <button className="btn" onClick={() => startEdit(b)}>
                    Настроить
                  </button>
                ) : null}
                <button
                  className="btn"
                  onClick={() => {
                    if (
                      !confirm(
                        `Удалить бренд «${b.name}» и все его находки? Действие необратимо.`,
                      )
                    )
                      return;
                    void (async () => {
                      try {
                        const r = await fetch(`/api/brands/${b.id}`, { method: "DELETE" });
                        if (!r.ok) throw new Error(String(r.status));
                        toast(`Бренд «${b.name}» удалён`, "ok");
                        reload();
                      } catch (e) {
                        toast(`Не удалось удалить: ${e instanceof Error ? e.message : e}`, "err");
                      }
                    })();
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="panel">
          <h3>Новый бренд</h3>
          <label className="field">
            <span>Название</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Acme Textiles"
            />
          </label>
          <label className="field">
            <span>Запросы (по одному на строку)</span>
            <textarea
              rows={6}
              value={queriesText || suggested.join("\n")}
              onChange={(e) => setQueriesText(e.target.value)}
              placeholder="ключи поиска"
            />
          </label>
          <label className="field">
            <span>Google Alerts / RSS (необязательно)</span>
            <textarea
              rows={3}
              value={feedsText}
              onChange={(e) => setFeedsText(e.target.value)}
              placeholder="https://www.google.com/alerts/feeds/..."
            />
          </label>
          <div className="field">
            <span>Источники скана</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(defaultSources.length ? defaultSources : Object.keys(SOURCE_LABELS)).map(
                (id) => (
                  <label key={id} style={{ display: "flex", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={sources.includes(id)}
                      onChange={() => toggleSource(sources, id, setSources)}
                    />
                    {SOURCE_LABELS[id] || id}
                  </label>
                ),
              )}
            </div>
          </div>
          {suggested.length ? (
            <p className="hint">
              Подсказки сгенерированы из названия — можно править.
            </p>
          ) : null}
          <button
            className="btn primary"
            disabled={busy || !name.trim()}
            onClick={() => {
              void (async () => {
                setBusy(true);
                setMsg("");
                try {
                  const queries = parseLines(queriesText || suggested.join("\n"));
                  const r = await fetch("/api/brands", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      name,
                      queries,
                      sources,
                      alerts_feed_urls: parseLines(feedsText),
                      scan: true,
                    }),
                  });
                  const d = await r.json();
                  if (!r.ok) throw new Error(d.error || "fail");
                  setName("");
                  setQueriesText("");
                  setFeedsText("");
                  setSources([...defaultSources]);
                  const scanHint = d.scan?.busy
                    ? " (скан занят — запустите вручную)"
                    : d.scan
                      ? ` · скан +${d.scan.inserted ?? 0}`
                      : "";
                  setMsg(`Бренд создан и выбран${scanHint}`);
                  await reload();
                } catch (e) {
                  setMsg(e instanceof Error ? e.message : String(e));
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            Создать и просканировать
          </button>
          {msg ? <p className="hint">{msg}</p> : null}
          <p className="hint">
            Активный бренд: {activeId ?? "—"}
            {brandQuery(activeId)}
          </p>
        </div>
      </div>
    </>
  );
}
